import React, { useState } from "react";
import { Check, RotateCcw, Ban, ArrowUpCircle, ClipboardCheck, XCircle, UserCheck2 } from "lucide-react";
import { Section } from "./Section";
import { StatusBadge } from "../StatusBadge";
import { formatJst } from "../../lib/date";
import { InterviewRescheduleForm } from "./InterviewRescheduleForm";
import { RecordOutcomeDialog } from "./RecordOutcomeDialog";
import { RejectApplicantDialog } from "./RejectApplicantDialog";
import { CancelInterviewDialog } from "./CancelInterviewDialog";
import { ConvertConfirmDialog } from "./ConvertConfirmDialog";

const ADVANCE_MAP = {
  NEW: "SCREENING",
  SCREENING: "INTERVIEWED",
  INTERVIEW_SCHEDULED: "INTERVIEWED",
  INTERVIEWED: "OFFER_EXTENDED",
  OFFER_EXTENDED: "HIRED",
};
const ACTIVE_BOOKING_STATUSES = ["HELD", "PENDING_CONFIRMATION", "CONFIRMED", "RESCHEDULE_REQUESTED"];

/** The right-hand action panel of the Applicant Detail master-detail layout - every mutating
 * action here is an explicit, distinctly-labeled button (never a generic "Manage"/"Action"
 * dropdown), and Reject/Cancel Interview/Convert always go through a confirmation dialog. */
export const ApplicantActionPanel = ({
  applicant, job, activeBooking, canManageInterviews, interviewOutcomeLabel,
  onAdvance, onReject, onWithdraw, onConfirmInterview, onRescheduleInterview, onProposeTime,
  onCancelInterview, onRecordComplete, onRecordNoShow, onConvert, onAddNote,
}) => {
  const [rescheduleMode, setRescheduleMode] = useState(null); // null | "RESCHEDULE" | "PROPOSE"
  const [showOutcomeDialog, setShowOutcomeDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [noteInput, setNoteInput] = useState("");

  const isTerminal = ["HIRED", "REJECTED", "WITHDRAWN"].includes(applicant.status);
  const nextStatus = ADVANCE_MAP[applicant.status];
  const canConvert = ["OFFER_EXTENDED", "HIRED"].includes(applicant.status) && !applicant.converted_application_id;
  const bookingIsActive = activeBooking && ACTIVE_BOOKING_STATUSES.includes(activeBooking.status);

  return (
    <div className="space-y-4" data-testid="applicant-action-panel">
      <Section title="Pipeline Stage" testId="panel-pipeline-stage">
        <div className="space-y-2">
          <StatusBadge status={applicant.status} />
          <p className="text-xs text-gray-500"><span className="text-gray-500">Recruiter:</span> {applicant.reviewer ? applicant.reviewer : "Not yet assigned"}</p>
          <p className="text-xs text-gray-500"><span className="text-gray-500">Store:</span> {job?.store_location || "Organization-wide"}</p>
          <p className="text-xs" data-testid="panel-workauth-review-status"><span className="text-gray-500">Work authorization review:</span> {applicant.work_authorization_review_required ? <span className="text-red-500 font-semibold">Required</span> : <span className="text-emerald-600 font-semibold">Not flagged</span>}</p>
          <p className="text-xs" data-testid="panel-availability-review-status"><span className="text-gray-500">Availability review:</span> {applicant.availability_review_required ? <span className="text-amber-600 font-semibold">Required</span> : <span className="text-emerald-600 font-semibold">Not flagged</span>}</p>
        </div>
      </Section>

      <Section title="Interview" testId="panel-interview">
        {!activeBooking ? (
          <p className="text-xs text-gray-500" data-testid="panel-no-active-interview">No active interview scheduled.</p>
        ) : (
          <div className="space-y-2">
            <StatusBadge status={activeBooking.status} />
            {activeBooking.interview_mode === "CONTACT_ME" ? (
              <p className="text-sm text-gray-600">Applicant asked management to contact them directly.</p>
            ) : (
              <p className="text-sm text-gray-700">{activeBooking.date} @ {activeBooking.start_time} JST</p>
            )}

            {canManageInterviews && !isTerminal && bookingIsActive && !rescheduleMode && (
              <div className="flex flex-col gap-1.5 pt-1">
                {activeBooking.status === "PENDING_CONFIRMATION" && (
                  <button data-testid="action-confirm-interview" onClick={onConfirmInterview} className="flex items-center justify-center gap-1.5 py-2 rounded-full bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600">
                    <Check size={13} /> Confirm Interview
                  </button>
                )}
                {activeBooking.status === "CONFIRMED" && (
                  <button data-testid="action-record-outcome" onClick={() => setShowOutcomeDialog(true)} className="flex items-center justify-center gap-1.5 py-2 rounded-full bg-blue-950 text-white text-xs font-semibold hover:bg-blue-900">
                    <ClipboardCheck size={13} /> Record Interview Outcome
                  </button>
                )}
                <button data-testid="action-propose-new-time" onClick={() => setRescheduleMode("PROPOSE")} className="flex items-center justify-center gap-1.5 py-2 rounded-full border border-amber-400 text-amber-600 text-xs font-semibold hover:bg-amber-50">
                  Propose New Time
                </button>
                <button data-testid="action-reschedule-interview" onClick={() => setRescheduleMode("RESCHEDULE")} className="flex items-center justify-center gap-1.5 py-2 rounded-full border border-blue-950 text-blue-950 text-xs font-semibold hover:bg-blue-50">
                  <RotateCcw size={13} /> Reschedule Interview
                </button>
                <button data-testid="action-cancel-interview" onClick={() => setShowCancelDialog(true)} className="flex items-center justify-center gap-1.5 py-2 rounded-full border border-red-300 text-red-600 text-xs font-semibold hover:bg-red-50">
                  <Ban size={13} /> Cancel Interview
                </button>
              </div>
            )}
            {rescheduleMode && (
              <InterviewRescheduleForm
                booking={activeBooking}
                mode={rescheduleMode}
                onCancel={() => setRescheduleMode(null)}
                onSubmit={async (date, time) => {
                  const ok = rescheduleMode === "PROPOSE" ? await onProposeTime(date, time) : await onRescheduleInterview(date, time);
                  if (ok) setRescheduleMode(null);
                }}
              />
            )}
          </div>
        )}
      </Section>

      <Section title="Internal Notes" testId="panel-internal-notes">
        <div className="space-y-2 mb-3 max-h-40 overflow-y-auto" data-testid="panel-notes-list">
          {(applicant.review_notes || []).map((n, i) => (
            <div key={i} className="text-xs text-gray-600 border-l-2 border-gray-200 pl-2">
              <span className="font-semibold">{n.by_name}</span> - {formatJst(n.at)}: {n.note}
            </div>
          ))}
          {(applicant.review_notes || []).length === 0 && <p className="text-xs text-gray-500">No internal notes yet.</p>}
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="panel-note-input"
            value={noteInput}
            onChange={(e) => setNoteInput(e.target.value)}
            placeholder="Add an internal note"
            className="flex-1 px-2.5 py-1.5 rounded-lg border border-gray-300 text-xs"
          />
          <button
            data-testid="panel-add-note-button"
            onClick={async () => {
              if (!noteInput.trim()) return;
              if (await onAddNote(noteInput.trim())) setNoteInput("");
            }}
            className="px-3 py-1.5 rounded-lg bg-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-300"
          >
            Add
          </button>
        </div>
      </Section>

      <Section title="Primary Actions" testId="panel-primary-actions">
        <div className="space-y-1.5">
          {!isTerminal && nextStatus && (
            <button data-testid="action-advance-applicant" onClick={onAdvance} className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-gray-900 text-white text-xs font-semibold hover:bg-gray-800">
              <ArrowUpCircle size={14} /> Advance Applicant to {nextStatus.replaceAll("_", " ")}
            </button>
          )}
          {applicant.converted_application_id ? (
            <p className="text-xs text-emerald-700" data-testid="panel-already-converted">Already converted to Employee Application <span className="font-mono">{applicant.converted_application_id}</span>.</p>
          ) : canConvert ? (
            <button data-testid="action-convert-to-application" onClick={() => setShowConvertDialog(true)} className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700">
              <UserCheck2 size={14} /> Convert to Employee Application
            </button>
          ) : (
            <p className="text-xs text-gray-500" data-testid="panel-cannot-convert">Only OFFER_EXTENDED or HIRED applicants can be converted.</p>
          )}
          {!isTerminal && (
            <button data-testid="action-reject-application" onClick={() => setShowRejectDialog(true)} className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-full border border-red-300 text-red-600 text-xs font-semibold hover:bg-red-50">
              <XCircle size={14} /> Reject Application
            </button>
          )}
          {!isTerminal && (
            <button data-testid="action-mark-withdrawn" onClick={onWithdraw} className="w-full text-center text-xs text-gray-500 hover:text-gray-600 hover:underline pt-0.5">
              Mark applicant as withdrawn
            </button>
          )}
        </div>
      </Section>

      <Section title="Audit Summary" testId="panel-audit-summary">
        <div className="space-y-1 text-xs text-gray-500">
          <p><span className="text-gray-500">Applied:</span> {formatJst(applicant.created_at)}</p>
          <p><span className="text-gray-500">Last reviewed:</span> {applicant.reviewed_at ? formatJst(applicant.reviewed_at) : "Not yet reviewed"}</p>
          {applicant.rejected_reason && <p><span className="text-gray-500">Rejection reason:</span> {applicant.rejected_reason}</p>}
        </div>
      </Section>

      <RecordOutcomeDialog open={showOutcomeDialog} onOpenChange={setShowOutcomeDialog} onComplete={async () => { if (await onRecordComplete()) setShowOutcomeDialog(false); }} onNoShow={async () => { if (await onRecordNoShow()) setShowOutcomeDialog(false); }} />
      <RejectApplicantDialog open={showRejectDialog} onOpenChange={setShowRejectDialog} onConfirm={async (reason) => { if (await onReject(reason)) setShowRejectDialog(false); }} />
      <CancelInterviewDialog open={showCancelDialog} onOpenChange={setShowCancelDialog} onConfirm={async (reason) => { if (await onCancelInterview(reason)) setShowCancelDialog(false); }} />
      <ConvertConfirmDialog open={showConvertDialog} onOpenChange={setShowConvertDialog} applicant={applicant} job={job} interviewOutcomeLabel={interviewOutcomeLabel} onConfirm={async () => { if (await onConvert()) setShowConvertDialog(false); }} />
    </div>
  );
};
